import { DateTime } from 'luxon';
import type {
  EvergreenInteractionOutsideVideo,
  EvergreenMp4ReplacementType,
  EvergreenMp4UploadInitInput,
  EvergreenTimelineEvent,
  EvergreenVideoCut,
  WebinarSessionConfigurationSession,
} from '@/features/webinar/types';

type SessionSlot = Pick<WebinarSessionConfigurationSession, 'date' | 'status'>;

function isDisplayableInteraction(
  event: EvergreenTimelineEvent,
): event is EvergreenTimelineEvent & { eventType: EvergreenInteractionOutsideVideo['eventType'] } {
  switch (event.eventType) {
    case 'OFFER':
    case 'SURVEY':
    case 'QUIZ':
    case 'PURCHASE_NOTIFICATION':
      return true;
    case 'RESOURCE_SHOW':
      return event.resourceType === 'SHARED_RESOURCE';
    default:
      return false;
  }
}

/**
 * Même règle que la promotion côté back : une interaction sans fin compte à son début. La durée lue
 * par le navigateur est fractionnaire, on l'arrondit au-dessus pour ne jamais bloquer ce que le back
 * accepterait.
 */
export function findInteractionsBeyondVideo(
  timeline: EvergreenTimelineEvent[],
  durationSeconds: number,
): EvergreenInteractionOutsideVideo[] {
  const limitSeconds = Math.ceil(durationSeconds);
  return timeline
    .filter(isDisplayableInteraction)
    .filter((event) => (event.endOffsetSeconds ?? event.startOffsetSeconds) > limitSeconds)
    .map((event) => ({
      eventId: event.id,
      eventType: event.eventType,
      startOffsetSeconds: event.startOffsetSeconds,
      endOffsetSeconds: event.endOffsetSeconds,
    }));
}

export type ReplacementCheckFailure = 'DURATION_UNREADABLE' | 'TIMELINE_UNAVAILABLE';

export type ReplacementCheck =
  | { kind: 'ok' }
  | { kind: 'blocked'; interactions: EvergreenInteractionOutsideVideo[] }
  | { kind: 'failed'; reason: ReplacementCheckFailure };

/** Une durée ou une timeline manquante n'est jamais un feu vert : le contrôle doit avoir eu lieu. */
export function checkReplacementAgainstTimeline(
  durationSeconds: number | null,
  timeline: EvergreenTimelineEvent[] | null,
): ReplacementCheck {
  if (durationSeconds == null) return { kind: 'failed', reason: 'DURATION_UNREADABLE' };
  if (timeline == null) return { kind: 'failed', reason: 'TIMELINE_UNAVAILABLE' };
  const interactions = findInteractionsBeyondVideo(timeline, durationSeconds);
  return interactions.length > 0 ? { kind: 'blocked', interactions } : { kind: 'ok' };
}

export const EVERGREEN_MP4_REPLACEMENT_TYPES: readonly EvergreenMp4ReplacementType[] = [
  'INDEPENDENT_VIDEO',
  'PREVIOUS_VIDEO_UNCUT',
  'PREVIOUS_VIDEO_WITH_CUTS',
];

/** Seule une version raccourcie porte des coupures : le back les refuse pour les autres choix. */
export type Mp4ReplacementDeclaration =
  | { replacementType: 'INDEPENDENT_VIDEO' | 'PREVIOUS_VIDEO_UNCUT' }
  | { replacementType: 'PREVIOUS_VIDEO_WITH_CUTS'; cuts: EvergreenVideoCut[] };

/**
 * Sans choix, pas de déclaration. Les coupures saisies puis abandonnées pour un autre choix ne sont
 * jamais transmises.
 */
export function buildReplacementDeclaration(
  replacementType: EvergreenMp4ReplacementType | null,
  cuts: readonly EvergreenVideoCut[] | null,
): Mp4ReplacementDeclaration | null {
  switch (replacementType) {
    case null:
      return null;
    case 'INDEPENDENT_VIDEO':
    case 'PREVIOUS_VIDEO_UNCUT':
      return { replacementType };
    case 'PREVIOUS_VIDEO_WITH_CUTS':
      return cuts && cuts.length > 0 ? { replacementType, cuts: [...cuts] } : null;
  }
}

/** Une version raccourcie n'est confirmable qu'une fois ses coupures appliquées à la timeline. */
export function canConfirmReplacement({
  declaration,
  check,
  cutsApplied,
}: {
  declaration: Mp4ReplacementDeclaration | null;
  check: ReplacementCheck;
  cutsApplied: boolean;
}): boolean {
  if (declaration == null || check.kind !== 'ok') return false;
  return declaration.replacementType !== 'PREVIOUS_VIDEO_WITH_CUTS' || cutsApplied;
}

/** Un premier import n'a pas de déclaration : sa demande reste celle d'avant les remplacements. */
export function withReplacementDeclaration(
  input: EvergreenMp4UploadInitInput,
  declaration: Mp4ReplacementDeclaration | null | undefined,
): EvergreenMp4UploadInitInput {
  if (!declaration) return input;
  const file = { fileName: input.fileName, mimeType: input.mimeType, sizeBytes: input.sizeBytes };
  return declaration.replacementType === 'PREVIOUS_VIDEO_WITH_CUTS'
    ? { ...file, replacementType: declaration.replacementType, cuts: declaration.cuts }
    : { ...file, replacementType: declaration.replacementType };
}

export function hasSessionInProgress(sessions: SessionSlot[] | undefined): boolean {
  return (sessions ?? []).some((session) => session.status === 'IN_PROGRESS');
}

export function findNextUpcomingSessionDate(
  sessions: SessionSlot[] | undefined,
  now: Date,
): string | null {
  const upcoming = (sessions ?? [])
    .filter((session) => session.status === 'UPCOMING')
    .map((session) => ({
      date: session.date,
      time: DateTime.fromISO(session.date, { zone: 'utc' }).toMillis(),
    }))
    .filter((session) => Number.isFinite(session.time) && session.time > now.getTime())
    .sort((a, b) => a.time - b.time);
  return upcoming[0]?.date ?? null;
}
